// src/pages/CalendarPage.jsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  subMonths,
  addMonths,
  startOfWeek,
  endOfWeek
} from 'date-fns';

const CalendarPage = () => {
  const tasks = useSelector(state => state.tasks);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const daysInCalendar = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getTasksForDate = (date) => {
    return tasks.filter(task => isSameDay(parseISO(task.dueDate), date));
  };

  const getPriorityStyle = (task) => {
    if (task.completed) return 'bg-green-900 text-green-200 border-green-700 hover:bg-green-800';
    switch (task.priority) {
      case 'high': return 'bg-red-900 text-red-200 border-red-700 hover:bg-red-800';
      case 'medium': return 'bg-yellow-900 text-yellow-200 border-yellow-700 hover:bg-yellow-800';
      case 'low':
      default: return 'bg-gray-800 text-gray-200 border-gray-700 hover:bg-gray-700';
    }
  };

  const isCurrentMonth = (date) => date.getMonth() === currentMonth.getMonth();

  return (
    <div className="relative mt-16 sm:mt-20 min-h-screen bg-darkslate overflow-hidden">
      {/* Brighter Decorative Background Lines */}
      <svg className="absolute top-0 left-0 w-full max-w-[900px] opacity-30 sm:opacity-50 z-0" viewBox="0 0 900 300" fill="none">
        <path d="M0,150 C150,50 300,250 450,150 C600,50 750,250 900,150" stroke="#FF6B6B" strokeWidth="6" strokeLinecap="round" fill="none" />
      </svg>
      <svg className="absolute bottom-0 right-0 w-full max-w-[900px] opacity-30 sm:opacity-50 z-0" viewBox="0 0 900 300" fill="none">
        <path d="M0,150 C150,250 300,50 450,150 C600,250 750,50 900,150" stroke="#C084FC" strokeWidth="6" strokeLinecap="round" fill="none" />
      </svg>

      <div className="relative z-10">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Header Section */}
          <div className={`
            bg-zinc-900 text-white rounded-xl sm:rounded-2xl shadow-xl border border-zinc-700 mb-6 sm:mb-8 p-4 sm:p-6 lg:p-8
            transform transition-all duration-1000 ease-in-out
            ${isLoaded ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}
          `}>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="group flex items-center space-x-2 bg-zinc-800 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-zinc-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full sm:w-auto justify-center"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium text-sm sm:text-base">Previous</span>
              </button>

              <div className="text-center order-first sm:order-none">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight">
                  {format(currentMonth, 'MMMM')}
                </h1>
                <p className="text-lg sm:text-xl text-zinc-400 font-light mt-1">
                  {format(currentMonth, 'yyyy')}
                </p>
              </div>

              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="group flex items-center space-x-2 bg-zinc-800 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-zinc-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full sm:w-auto justify-center"
              >
                <span className="font-medium text-sm sm:text-base">Next</span>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className={`
            bg-zinc-900 text-white rounded-xl sm:rounded-2xl shadow-xl border border-zinc-700 overflow-hidden
            transform transition-all duration-1000 ease-in-out delay-400
            ${isLoaded ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-4 opacity-0 scale-95'}
          `}>
            <div className="grid grid-cols-7 bg-zinc-800">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                <div key={day} className="p-2 sm:p-3 text-center font-semibold text-xs sm:text-sm tracking-wide text-white">
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.slice(0, 1)}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {daysInCalendar.map((day, index) => {
                const dayTasks = getTasksForDate(day);
                const isToday = isSameDay(day, new Date());
                const isInCurrentMonth = isCurrentMonth(day);
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                return (
                  <div
                    key={day.toISOString()}
                    className={`
                      border border-zinc-800 min-h-[80px] sm:min-h-[100px] lg:min-h-[120px] p-1 sm:p-2
                      ${!isInCurrentMonth ? 'bg-zinc-800 text-zinc-500' : 'bg-zinc-900'}
                      ${isToday ? 'border-blue-500' : ''}
                      ${isWeekend ? 'bg-zinc-800' : ''}
                      ${isLoaded ? 'opacity-100' : 'opacity-0'}
                      transition-all duration-700 ease-in-out
                    `}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs sm:text-sm font-bold">
                        {format(day, 'd')}
                      </div>
                      {dayTasks.length > 0 && (
                        <div className="bg-zinc-700 text-white text-xs px-1 sm:px-2 py-0.5 rounded-full font-medium">
                          {dayTasks.length}
                        </div>
                      )}
                    </div>

                    <div className="space-y-0.5 sm:space-y-1">
                      {dayTasks.slice(0, window.innerWidth < 640 ? 2 : 3).map((task, i) => (
                        <div
                          key={task.id}
                          className={`text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded border ${getPriorityStyle(task)} truncate`}
                          title={task.title}
                        >
                          <span className="hidden sm:inline">{task.title}</span>
                          <span className="sm:hidden">{task.title.slice(0, 8)}...</span>
                        </div>
                      ))}
                      {dayTasks.length > (window.innerWidth < 640 ? 2 : 3) && (
                        <div className="text-xs italic text-zinc-400">
                          +{dayTasks.length - (window.innerWidth < 640 ? 2 : 3)} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Priority Legend */}
          <div className={`
            mt-6 sm:mt-8 bg-zinc-900 rounded-xl sm:rounded-2xl shadow-lg border border-zinc-700 p-4 sm:p-6
            transform transition-all duration-800 ease-in-out delay-1000
            ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
          `}>
            <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Priority Legend</h3>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-6">
              {[
                { color: 'bg-red-900 border-red-700', label: 'High Priority' },
                { color: 'bg-yellow-900 border-yellow-700', label: 'Medium Priority' },
                { color: 'bg-gray-800 border-gray-700', label: 'Low Priority' },
                { color: 'bg-green-900 border-green-700', label: 'Completed' }
              ].map(item => (
                <div key={item.label} className="flex items-center space-x-2">
                  <div className={`w-3 h-3 sm:w-4 sm:h-4 ${item.color} rounded border`}></div>
                  <span className="text-xs sm:text-sm text-zinc-300">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;